import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { LiveSummary } from '@multimod/shared';
import { parseYouTubeUrl } from './parseYouTubeUrl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function YouTubeLiveInput({
  onResolved,
}: {
  onResolved: (live: LiveSummary) => void;
}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: async (videoId: string) => {
      const r = await fetch(`/live-discovery/youtube/resolve?videoId=${encodeURIComponent(videoId)}`, {
        credentials: 'include',
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'unknown');
      }
      const body = (await r.json()) as { live: LiveSummary };
      return body.live;
    },
    onSuccess: (live) => {
      onResolved(live);
      setValue('');
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const id = parseYouTubeUrl(value);
    if (!id) {
      setError('invalid_url');
      return;
    }
    mutation.mutate(id);
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2">
      <label className="text-sm text-fg-muted" htmlFor="yt-url">
        URL ou ID da live YouTube
      </label>
      <div className="flex gap-2">
        <Input
          id="yt-url"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="https://youtube.com/watch?v=..."
          disabled={mutation.isPending}
        />
        <Button type="submit" disabled={mutation.isPending || !value.trim()}>
          Adicionar
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{messageForError(error)}</p>}
    </form>
  );
}

function messageForError(code: string): string {
  switch (code) {
    case 'invalid_url': return 'URL inválida. Cole um link de live do YouTube ou o ID (11 caracteres).';
    case 'youtube_not_connected': return 'Conecte sua conta YouTube primeiro.';
    case 'not_live': return 'Essa live não está ativa.';
    case 'quota_exhausted': return 'Limite diário da API YouTube atingido — reseta às 04:00 UTC.';
    case 'invalid_video_id': return 'Formato de ID inválido.';
    default: return 'Falha ao resolver a live. Tente novamente.';
  }
}
